import React, { useState, useEffect } from 'react'
import moment from 'moment'
import { AgreementInfoFormProps } from './types'
import { PageTitle } from './PageTitle'
import cl from 'classnames'
import styles from './AgreementInfoForm.module.scss'
import { SectionContainer } from './SectionContainer'
import { SectionInput } from './SectionInput'
import { Row } from './Row'
import { ToggleButton } from './ToggleButton'
import { LabeledContainer } from './LabeledContainer'
import { ScopesSection } from './ScopesSection'
import { newAgreement } from './agreement-model-templates'
import { Agreement, AgreementStatusTypes, AgreementItemTypes, AgreementItemPropertyTypes, FilterTypes } from 'models'
import { validateAgreementForm } from './utils'
import { GENERAL_STORE, AGREEMENT_STORE } from 'store'
import { inject } from 'mobx-react'
import { Button } from 'components/common'
import PeriodPicker from './PeriodPicker'
import { getRemainingBudget } from '../AgreementsTable'

const AgreementInfoFormComponent = (props: AgreementInfoFormProps) => {
  const {
    agreement,
    [GENERAL_STORE]: { showError, push },
    [AGREEMENT_STORE]: { createAgreement, editAgreement }
  } = props
  const [formState, setFormState] = useState<Agreement>(newAgreement)
  const [isScrollOnTop, setIsScrollOnTop] = useState(true)
  const editing = agreement && JSON.stringify(agreement) !== '{}'
  useEffect(() => {
    editing && setFormState({
      ...agreement,
      period: {
        start: moment(agreement.startDate),
        end: moment(agreement.endDate)
      }
    })
  }, [agreement, editing])

  const handleScroll = (event: React.UIEvent<HTMLFormElement>) => {
    const isScrollOnTopNow = event.currentTarget.scrollTop === 0
    if (isScrollOnTop !== isScrollOnTopNow) setIsScrollOnTop(isScrollOnTopNow)
  }

  const handleSubmit = () => {
    const errMessage = validateAgreementForm(formState)
    if (errMessage) {
      showError(errMessage)
      return
    }

    const parsedForm = {
      ...formState,
      name: formState.name.trim(),
      startDate: formState.period.start.valueOf(),
      endDate: formState.period.end.valueOf(),
      scopes: formState.scopes.map(scope => ({ ...scope, name: scope.name.trim() }))
    }

    parsedForm.scopes.forEach(scope => {
      const index = scope.filters.findIndex(filter => filter.type === FilterTypes.PROJECTS)
      if (index < 0) return
      scope.filters[index].projects = scope.filters[index].projects.filter(p => p.id.toString() !== 'all')
    })

    editing
      ? editAgreement(parsedForm)
      : createAgreement(parsedForm)
  }

  const getItemPropertyValue = (
    itemType: AgreementItemTypes,
    itemProperty: AgreementItemPropertyTypes
  ) => {
    const item = formState.items.find(itm => itm.type === itemType)
    return item.properties[itemProperty]
  }

  const handleItemPropertyChange = (
    itemType: AgreementItemTypes,
    itemProperty: AgreementItemPropertyTypes
  ) =>
    (value: string) => {
      const itemIndex = formState.items.findIndex(itm => itm.type === itemType)
      const newState = { ...formState }
      newState.items[itemIndex].properties[itemProperty] = Number(value)
      setFormState(newState)
    }

  return (
    <div className={styles.container}>
      <PageTitle
        text={editing ? 'Edit Agreement' : 'Add a New Agreement'}
        overEffect={!isScrollOnTop}
      />
      <form
        className={cl(styles.form, styles.scrollable)}
        onScroll={handleScroll}
      >

        <SectionContainer>
          <Row>
            <SectionInput
              label='name'
              className={styles.flex1}
              placeholder='Type the name of the agreement'
              onChange={name => setFormState({ ...formState, name })}
              value={formState.name}
            />
            <ToggleButton
              label='Status'
              className={styles.toggleButton}
              isSwitchedOn={formState.status === AgreementStatusTypes.ACTIVE}
              disabled
            />
            <ToggleButton
              label='Effect on Elements'
              className={styles.toggleButton}
              isSwitchedOn={formState.effectOnElements}
              onClick={() => setFormState(
                {
                  ...formState,
                  effectOnElements: !formState.effectOnElements
                }
              )}
            />

            {editing && <div className={styles.budgetRemaining}>
              <label>Budget remaining</label>
              <div>${getRemainingBudget(agreement)}</div>
            </div>}
          </Row>
          <Row>
            <SectionInput
              className={styles.rowChild}
              label='budget'
              entity='$'
              placeholder='$'
              onChange={budget => setFormState({ ...formState, budget: Number(budget) })}
              value={(formState.budget || '').toString()}
              type='number'
              min={0}
            />
            <SectionInput
              className={styles.rowChild}
              label='buffer'
              entity='%'
              placeholder='%'
              onChange={budgetBuffer =>
                setFormState({ ...formState, budgetBuffer: Number(budgetBuffer) })
              }
              value={(formState.budgetBuffer || '').toString()}
              type='number'
              min={0}
            />
            <PeriodPicker
              period={formState.period}
              setPeriod={period => setFormState({ ...formState, period })}
              // className={styles.rowChild}
              label='period'
            />
          </Row>
        </SectionContainer>

        <SectionContainer label='Configuration' innerTitle='Blob settings'>
          <Row>
            <Row className={styles.rowChild} style={{ marginBottom: 0 }}>
              <SectionInput
                label='Blob triggered'
                className={styles.rowChild}
                value={getItemPropertyValue(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.BLOB_TRIGGERED
                )}
                onChange={handleItemPropertyChange(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.BLOB_TRIGGERED
                )}
                type='number'
                min={0}
              />
              <SectionInput
                label='Max attach time'
                className={styles.rowChild}
                value={getItemPropertyValue(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS
                )}
                onChange={handleItemPropertyChange(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS
                )}
                type='number'
                min={0}
              />
            </Row>
            <LabeledContainer
              label='Price per time on screen'
              className={styles.rowChild}
            >
              <SectionInput
                className={styles.rowChild}
                value={getItemPropertyValue(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED
                )}
                onChange={handleItemPropertyChange(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED
                )}
                type='number'
                min={0}
              />
              <SectionInput
                className={styles.rowChild}
                value={getItemPropertyValue(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED
                )}
                onChange={handleItemPropertyChange(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED
                )}
                type='number'
                min={0}
              />
            </LabeledContainer>
            <LabeledContainer
              label='Time in stack'
              className={styles.rowChild}
            // className={styles.flex2}s
            >
              <SectionInput
                className={styles.rowChild}
                value={getItemPropertyValue(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK
                )}
                onChange={handleItemPropertyChange(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK
                )}
                type='number'
                min={0}
              />
              <SectionInput
                className={styles.rowChild}
                value={getItemPropertyValue(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK
                )}
                onChange={handleItemPropertyChange(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK
                )}
                type='number'
                min={0}
              />
            </LabeledContainer>
          </Row>
        </SectionContainer>

        <Row style={{ marginTop: 30 }}>
          <SectionContainer innerTitle='Product' style={{ flex: 2 }}>
            <Row>
              <SectionInput
                label='Product page opened from blob'
                layout='horizontal'
                className={styles.flex1}
                value={getItemPropertyValue(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB
                )}
                onChange={handleItemPropertyChange(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB
                )}
                type='number'
                min={0}
              />
              <SectionInput
                label='Product added to cart'
                layout='horizontal'
                className={styles.flex1}
                value={getItemPropertyValue(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART
                )}
                onChange={handleItemPropertyChange(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART
                )}
                type='number'
                min={0}
              />
            </Row>
          </SectionContainer>
          <SectionContainer innerTitle='Video commercial' style={{ marginTop: 0, flex: 1 }}>
            <SectionInput
              label='Price per video playback'
              layout='horizontal'
              value={getItemPropertyValue(
                AgreementItemTypes.VIDEO_COMMERCIAL,
                AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK
              )}
              onChange={handleItemPropertyChange(
                AgreementItemTypes.VIDEO_COMMERCIAL,
                AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK
              )}
              type='number'
              min={0}
            />
          </SectionContainer>
        </Row>

        <Row style={{ marginTop: 30 }}>
          <SectionContainer innerTitle='Poster' style={{ flex: 2 }}>
            <Row>
              <SectionInput
                label='Price per click'
                layout='horizontal'
                className={styles.flex1}
                value={getItemPropertyValue(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_CLICK
                )}
                onChange={handleItemPropertyChange(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_CLICK
                )}
                type='number'
                min={0}
              />
              <SectionInput
                label='Price per second on screen'
                layout='horizontal'
                className={styles.flex1}
                value={getItemPropertyValue(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN
                )}
                onChange={handleItemPropertyChange(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN
                )}
                type='number'
                min={0}
              />
            </Row>
          </SectionContainer>
          <SectionContainer innerTitle='Product purchase' style={{ marginTop: 0, flex: 1 }}>
            <SectionInput
              label='Purchase fee'
              layout='horizontal'
              value={getItemPropertyValue(
                AgreementItemTypes.PRODUCT_PURCHASE,
                AgreementItemPropertyTypes.PURCHASE_FEE
              )}
              onChange={handleItemPropertyChange(
                AgreementItemTypes.PRODUCT_PURCHASE,
                AgreementItemPropertyTypes.PURCHASE_FEE
              )}
              type='number'
              min={0}
            />
          </SectionContainer>
        </Row>

        <ScopesSection
          scopes={formState.scopes}
          setScopes={scopes => setFormState({ ...formState, scopes })}
        />

        <div className={styles.buttonsContainer}>
          <Button
            type='submit'
            size='medium'
            className={styles.saveBttn}
            onClick={handleSubmit}
          >
            Save Changes
          </Button>
          <Button
            type='cancel'
            size='medium'
            onClick={() => push('/agreements')}
          >
            Discard Changes
          </Button>
        </div>

      </form>
    </div>
  )
}

const AgreementInfoForm = inject(GENERAL_STORE, AGREEMENT_STORE)(AgreementInfoFormComponent)
export { AgreementInfoForm }
